import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Mail, AlertCircle, CheckCircle, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { apiRequest } from "../lib/utils";

export const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [resetLink, setResetLink] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await apiRequest("/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      
      setSuccess(true);
      // Sempre guarda o link de reset se estiver disponível
      if (response.reset_link) {
        setResetLink(response.reset_link);
      }
    } catch (err) {
      setError(err.message || "Erro ao processar solicitacao.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Form Section */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center">
              <CalendarDays className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                Conforme a Resolução
              </h1>
              <p className="text-sm text-slate-500">Gestão Documental</p>
            </div>
          </div>

          <Card className="border-0 shadow-none">
            <CardHeader className="px-0">
              <CardTitle className="text-xl" style={{ fontFamily: 'Manrope' }}>
                Recuperar Senha
              </CardTitle>
              <CardDescription>
                Digite seu email para receber as instrucoes de recuperação
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              {success ? (
                <div className="space-y-4">
                  <Alert className="bg-green-50 border-green-200">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-800">
                      Link de recuperação gerado com sucesso!
                    </AlertDescription>
                  </Alert>
                  
                  {/* Link de recuperação direto */}
                  {resetLink && (
                    <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
                      <p className="text-sm font-medium text-teal-800 mb-3">
                        Clique no botão abaixo para redefinir sua senha:
                      </p>
                      <a 
                        href={resetLink}
                        className="inline-flex items-center justify-center w-full px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
                        data-testid="reset-link-button"
                      >
                        Redefinir Minha Senha
                      </a>
                      <p className="text-xs text-teal-600 mt-2 text-center">
                        Este link expira em 1 hora
                      </p>
                    </div>
                  )}

                  <Link to="/login">
                    <Button variant="outline" className="w-full mt-4">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Voltar para Login
                    </Button>
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <Alert variant="destructive" data-testid="forgot-error">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="seu@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10"
                        required
                        data-testid="forgot-email"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-teal-600 hover:bg-teal-700"
                    disabled={loading}
                    data-testid="forgot-submit"
                  >
                    {loading ? "Enviando..." : "Enviar Instrucoes"}
                  </Button>

                  <Link to="/login">
                    <Button variant="ghost" className="w-full mt-2">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Voltar para Login
                    </Button>
                  </Link>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Hero Section */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-teal-600 to-teal-700 items-center justify-center p-12">
        <div className="max-w-lg text-white">
          <h2 className="text-4xl font-bold mb-6" style={{ fontFamily: 'Manrope' }}>
            Recupere seu acesso
          </h2>
          <p className="text-teal-100 text-lg mb-8">
            Não se preocupe, acontece com todo mundo. Vamos ajuda-lo a redefinir sua senha rápidamente.
          </p>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
            <h3 className="font-semibold mb-3">Como funciona:</h3>
            <ol className="space-y-2 text-teal-100">
              <li>1. Digite seu email cadastrado</li>
              <li>2. Receba o link de recuperação</li>
              <li>3. Crie uma nova senha segura</li>
              <li>4. Acesse normalmente o sistema</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
